<script setup lang="ts">
/**
 * Draws the glyph named by `Category.iconName`. The server is the source of that
 * name, so an unrecognised one is expected, not exceptional: it falls back to a
 * neutral mark rather than rendering nothing and collapsing the tile's layout.
 *
 * The names come from the taxonomy in `backend/prisma/categories.ts`.
 */
defineProps<{ name: string | null }>();
</script>

<template>
  <svg
    class="cat-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <template v-if="name === 'home'">
      <path d="M3 10.6 12 3.5l9 7.1" />
      <path d="M5.6 9.4V20.5h12.8V9.4" />
      <path d="M10 20.5v-5h4v5" />
    </template>

    <template v-else-if="name === 'car'">
      <path d="M5.2 15.4 6.8 11a2 2 0 0 1 1.9-1.3h6.6a2 2 0 0 1 1.9 1.3l1.6 4.4" />
      <path d="M4.5 15.4h15" />
      <path d="M5 15.4V18M19 15.4V18" />
      <circle cx="8" cy="17.6" r="1.5" />
      <circle cx="16" cy="17.6" r="1.5" />
    </template>

    <template v-else-if="name === 'sparkle'">
      <path d="M12 3.4l2 5.4 5.4 2-5.4 2-2 5.4-2-5.4-5.4-2 5.4-2Z" />
      <path d="M18.6 16.4l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7Z" />
    </template>

    <template v-else-if="name === 'monitor'">
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M12 16.5v3.5" />
      <path d="M8.5 20h7" />
    </template>

    <template v-else-if="name === 'users'">
      <circle cx="9.2" cy="8.6" r="3.2" />
      <path d="M3.6 19.6a5.6 5.6 0 0 1 11.2 0" />
      <path d="M16.4 6.2a3 3 0 0 1 0 5.7" />
      <path d="M17.6 14.4a5 5 0 0 1 3 5.2" />
    </template>

    <template v-else-if="name === 'truck'">
      <path d="M3 6.6h10.4v9.2H3z" />
      <path d="M13.4 10.2h3.4l3.2 3.2v2.4h-6.6z" />
      <circle cx="7.2" cy="17.6" r="1.6" />
      <circle cx="16.8" cy="17.6" r="1.6" />
    </template>

    <template v-else-if="name === 'party'">
      <path d="M4.4 19.8 9 8.6l6.4 6.4-11 4.8Z" />
      <path d="M14.6 4.2v2.1M18.4 5.8l-1.5 1.5M17.9 10.6h2.1" />
    </template>

    <template v-else-if="name === 'utensils'">
      <path d="M7.2 3.6v5.2a2.2 2.2 0 0 0 4.4 0V3.6" />
      <path d="M9.4 11v9.4" />
      <path d="M16.6 3.6c1.9 1.7 2.4 5.8 0 8.6v8.2" />
    </template>

    <template v-else-if="name === 'briefcase'">
      <rect x="3" y="7.4" width="18" height="11.2" rx="2" />
      <path d="M8.6 7.4V6a2 2 0 0 1 2-2h2.8a2 2 0 0 1 2 2v1.4" />
      <path d="M3 12.4h18" />
    </template>

    <template v-else-if="name === 'headset'">
      <path d="M4.8 14.2v-2a7.2 7.2 0 0 1 14.4 0v2" />
      <path d="M4.8 13.4h1.8v5.2H4.8a1.6 1.6 0 0 1-1.6-1.6v-2a1.6 1.6 0 0 1 1.6-1.6Z" />
      <path d="M19.2 13.4h-1.8v5.2h1.8a1.6 1.6 0 0 0 1.6-1.6v-2a1.6 1.6 0 0 0-1.6-1.6Z" />
      <path d="M19.2 18.6a2.4 2.4 0 0 1-2.4 2.4h-2.2" />
    </template>

    <template v-else-if="name === 'leaf'">
      <path d="M5 19.4C5 11 10.2 6.4 19.4 6.4c0 9.4-5 13.6-11 13.6-1.8 0-3.4-.2-3.4-.6Z" />
      <path d="M5.2 19.6C8 16 11.4 13.6 15.6 12" />
    </template>

    <template v-else-if="name === 'shield'">
      <path d="M12 3.3 19.4 6v5.9c0 4.2-3 7.3-7.4 8.7-4.4-1.4-7.4-4.5-7.4-8.7V6Z" />
      <path d="M9 12.1l2.2 2.2 4-4.2" />
    </template>

    <template v-else-if="name === 'book'">
      <path d="M4 5.4h5.5a2.5 2.5 0 0 1 2.5 2.5v11a2 2 0 0 0-2-2H4Z" />
      <path d="M20 5.4h-5.5a2.5 2.5 0 0 0-2.5 2.5v11a2 2 0 0 1 2-2H20Z" />
    </template>

    <template v-else-if="name === 'hard-hat'">
      <path d="M4.4 17.6h15.2" />
      <path d="M6.6 17.6v-2.8a5.5 5.5 0 0 1 2.9-4.8V6.4a1.5 1.5 0 0 1 1.5-1.5h2a1.5 1.5 0 0 1 1.5 1.5v3.6a5.5 5.5 0 0 1 2.9 4.8v2.8" />
    </template>

    <!-- Retained: an older seed named the Wellness category this. -->
    <template v-else-if="name === 'heart'">
      <path d="M12 20.2c-1.9-1.3-7.4-4.9-7.4-9a4.2 4.2 0 0 1 7.4-2.7 4.2 4.2 0 0 1 7.4 2.7c0 4.1-5.5 7.7-7.4 9Z" />
    </template>

    <template v-else>
      <circle cx="12" cy="12" r="7.5" />
    </template>
  </svg>
</template>

<style scoped>
.cat-icon { width: 24px; height: 24px; flex: none; }
</style>
